import { SpanContext } from '@opentelemetry/api';
import { updateDeviceStatus } from 'src/common/device/initialize-devices';
import { DeviceType } from 'src/common/enumerations/device-type';
import { EntryMethod } from 'src/common/enumerations/entry-method';
import { delay } from 'src/common/helpers/delay-helper';
import { PaymentException } from 'src/common/exceptions/paymentException';
import {
  EpsGetCardDetailsFailure,
  EpsPaymentFailure,
  EpsRefundFailure,
  EpsSignatureCancel,
  EpsSignatureFailure,
  EpsVoidFailure,
} from 'src/common/helpers/error-code';
import XLRLogger from 'src/common/helpers/logger';
import { createSpan, endSpan } from 'src/common/helpers/tracing-helper';
import { IDeviceStatusItem } from 'src/common/interfaces/states/IDeviceStatusItem';
import { store } from 'src/common/redux/configure-store';
import { DeviceStatusActions } from 'src/common/redux/reducers/device-status-reducer';
import { PinPadActions } from 'src/common/redux/reducers/pinpad-reducer';
import {
  buildActivateMessage,
  buildReloadMessage,
  CardDetails,
  EpsData,
  parseElectronicPaymentData,
  CardDetailsContext,
} from 'src/devices/payment/dtos/eps-data';
import {
  resetCardData,
  resetCardWaiting,
  setCancelled,
  setEnableCardEntryPerformed,
} from 'src/devices/payment/get-card-details';
import { emeraldSendPaymentRequest } from 'src/devices/payment/payment-controller';
import {
  buildBasicPaymentMessage,
  buildConfigOptions,
  buildGCDMessage,
  buildPurchaseMessage,
  buildReturnMessage,
  PaymentDetails,
  PaymentMessage,
  SignatureDetails,
} from './dtos/eps-data';
import { isPendingPaymentRequest } from '@ncr-retail/common-payment-operations';
import { getPinPadConfig } from './pinpad-utils';
import { PmiStatusCodes } from './pmi-status-codes-enums';
import { getCurrentXLRConfig } from 'src/xlr-configuration/xlr-config';

export const activateOnlineItem = async (
  authorizeOnlineItemData: authorizeOnlineItemDto,
  parentSpanContext?: SpanContext,
): Promise<PaymentMessage> => {
  const span = createSpan('[RPS] activate-online-item', parentSpanContext, true);
  const { externalId, price, entryMethod, upc } = authorizeOnlineItemData;

  try {
    const message = buildActivateMessage(externalId, price, entryMethod, upc);
    message.configOptions = buildConfigOptions();
    return emeraldSendPaymentRequest('activate', message, span.spanContext());
  } catch (err) {
    logError(err, 'CCL Activate Message Failed');
    throw err;
  } finally {
    endSpan(span);
  }
};

export const reloadOnlineItem = async (
  authorizeOnlineItemData: authorizeOnlineItemDto,
  parentSpanContext?: SpanContext,
): Promise<PaymentMessage> => {
  const span = createSpan('[RPS] reload-online-item', parentSpanContext, true);
  const { externalId, price, entryMethod } = authorizeOnlineItemData;

  try {
    const message = buildReloadMessage(externalId, price, entryMethod);
    message.configOptions = buildConfigOptions();
    return emeraldSendPaymentRequest('reload', message, span.spanContext());
  } catch (err) {
    logError(err, 'CCL Reload Message Failed');
    throw err;
  } finally {
    endSpan(span);
  }
};

export const voidPaymentforOnlineItem = async (
  externalId: string = '',
  referenceNumber: string = '',
  price: string,
  parentSpanContext?: SpanContext,
): Promise<PaymentMessage> => {
  const span = createSpan('[RPS] deactivate-online-item', parentSpanContext, true);
  try {
    const message = buildActivateMessage(externalId, price);
    message.configOptions = buildConfigOptions();
    message.paymentDetails = {
      paymentTranID: referenceNumber,
    };
    return emeraldSendPaymentRequest('void', message, span.spanContext());
  } catch (err) {
    logError(err, 'CCL Deactivate Message Failed');
    throw err;
  } finally {
    endSpan(span);
  }
};

export const initializePaymentService = async (parentSpanContext?: SpanContext): Promise<void> => {
  const span = createSpan('[RPS] initialize-payment-service', parentSpanContext, true);
  try {
    const message = buildBasicPaymentMessage();
    message.configOptions = buildConfigOptions();
    await emeraldSendPaymentRequest('initialize-service', message, span.spanContext());
    store.dispatch(PinPadActions.setIsPinpadConfigured(true));
    updateDeviceStatus({
      deviceName: getPinPadConfig()?.LogicalName ?? '',
      initialized: true,
      deviceType: DeviceType.CCL,
    });
  } catch (err) {
    logError(err, 'CCL Initialize Message Failed');
    isInEnablePayment = false;
    throw err;
  } finally {
    endSpan(span);
  }
};

export const restartPaymentSession = async (): Promise<void> => {
  if (inPaymentSession()) {
    await cancelPinpadOperation();
    await endPaymentSession();
    await delay(1000);
  }
  await beginPaymentSession();
};

export const openLane = async (): Promise<void> => {
  const span = createSpan('[RPS] open-lane', undefined, true);
  try {
    const message = buildBasicPaymentMessage();
    message.configOptions = buildConfigOptions();
    await emeraldSendPaymentRequest('open-lane', message, span.spanContext());
  } catch (err) {
    logError(err, 'CCL OpenLane Message Failed');
    isInEnablePayment = false;
    throw err;
  } finally {
    endSpan(span);
  }
};

export const closeLane = async (): Promise<void> => {
  const span = createSpan('[RPS] close-lane', undefined, true);
  try {
    await emeraldSendPaymentRequest('close-lane', buildBasicPaymentMessage(), span.spanContext());
  } catch (err) {
    logError(err, 'CCL CloseLane Message Failed');
  } finally {
    endSpan(span);
  }
};

let isInEnablePayment = false;

export const inEnablePayment = (): boolean => isInEnablePayment;

export const updatePinPadStatus = (isWarningActive: boolean, isInitialized: boolean): void => {
  const device: IDeviceStatusItem | undefined = store
    .getState()
    .deviceStatus.devices.find(device => device.deviceType === DeviceType.CCL);
  if (device) {
    store.dispatch(
      DeviceStatusActions.updateDevice({
        ...device,
        warning: isWarningActive,
        initialized: isInitialized,
      }),
    );
  }
};

export const enablePayments = async (): Promise<void> => {
  const span = createSpan('[RPS] enable-payments', undefined, true);
  try {
    const message = buildBasicPaymentMessage();
    message.configOptions = buildConfigOptions();
    await emeraldSendPaymentRequest('enable-payments', message, span.spanContext());
    store.dispatch(PinPadActions.setIsPinpadConfigured(true));
    updateDeviceStatus({
      deviceName: getPinPadConfig()?.LogicalName ?? '',
      initialized: true,
      deviceType: DeviceType.CCL,
    });
    isInEnablePayment = true;
  } catch (err) {
    isInEnablePayment = false;
    updatePinPadStatus(true, false);
    logError(err, 'CCL EnablePayments Message Failed');
  } finally {
    endSpan(span);
  }
};

export const disablePayments = async (): Promise<void> => {
  const span = createSpan('[RPS] disable-payments', undefined, true);
  try {
    await emeraldSendPaymentRequest(
      'disable-payments',
      buildBasicPaymentMessage(),
      span.spanContext(),
    );
    isInEnablePayment = false;
  } catch (err) {
    logError(err, 'CCL DisablePayments Message Failed');
  } finally {
    endSpan(span);
  }
};

export const clearPinPad = async (): Promise<void> => {
  if (inPaymentSession()) {
    await cancelPinpadOperation(); // We need sessionID and paymentTransID
    await endPaymentSession();
  }
  resetCardData();
  await disablePayments();

  /*
   * Relevant to Emerald GX, so pinpad swipe/tap will be disabled
   */
  const { shouldCallCardIdentificationNotExpected } = getCurrentXLRConfig();
  if (shouldCallCardIdentificationNotExpected) {
    resetCardWaiting();
  }
};

let isInPaymentSession = false;

export const inPaymentSession = (): boolean => isInPaymentSession;

export const beginPaymentSession = async (): Promise<void> => {
  const span = createSpan('[RPS] begin-payment-session', undefined, true);
  const transactionId = store.getState().transaction.transactionId;
  try {
    const message = buildBasicPaymentMessage();
    message.posTranID = transactionId;
    const beginPaymentSessionResponse = await emeraldSendPaymentRequest(
      'begin-payment-session',
      message,
      span.spanContext(),
    );
    store.dispatch(
      PinPadActions.setSessionTranId(
        beginPaymentSessionResponse.paymentDetails?.sessionTranID ?? '',
      ),
    );
    isInPaymentSession = true;
  } catch (err) {
    logError(err, 'CCL BeginPaymentSession Message Failed');
  } finally {
    endSpan(span);
  }
};

export const getCardDetailsFromPinPad = async (
  tenderedAmount: number,
  dueAmount: number,
  entryMethod?: EntryMethod,
  expectedCardDetailsContext?: CardDetailsContext,
): Promise<EpsData> => {
  const span = createSpan('[RPS] get-card-details', undefined, true);
  const msg: PaymentMessage = buildGCDMessage(
    tenderedAmount,
    dueAmount,
    entryMethod || '',
    expectedCardDetailsContext || CardDetailsContext.Payment,
  );
  return emeraldSendPaymentRequest('get-card-details', msg, span.spanContext())
    .then(message => {
      if (!message?.cardDetails?.length) {
        throw new PaymentException({
          code: EpsGetCardDetailsFailure,
          message: 'MissingCardDetails',
        });
      }
      return parseElectronicPaymentData(message);
    })
    .catch(async err => {
      await endPaymentSession().catch(() => {
        XLRLogger.debug({
          message: 'Unable to end payment session after get card details failure',
          source: 'pinpad-operations',
        });
      });
      logError(err, 'CCL GetCardDetails Message Failed');
      err.code = EpsGetCardDetailsFailure;
      throw err;
    })
    .finally(() => endSpan(span));
};

export const purchaseUsingPinPad = async (
  tenderedAmount: number,
  dueAmount: number,
  paymentTranId: string,
  cashbackAmount?: number,
): Promise<PaymentMessage> => {
  const span = createSpan('[RPS] pinpad-purchase', undefined, true);
  const rpsMessage = buildPurchaseMessage(tenderedAmount, dueAmount, paymentTranId, cashbackAmount);
  return emeraldSendPaymentRequest('purchase', rpsMessage, span.spanContext())
    .catch(err => {
      logError(err, 'CCL Purchase Message Failed');
      err.code = EpsPaymentFailure;
      throw err;
    })
    .finally(() => endSpan(span));
};

export const returnUsingPinPad = async (
  tenderedAmount: number,
  dueAmount: number,
  paymentTranId: string,
): Promise<PaymentMessage> => {
  const span = createSpan('[RPS] pinpad-refund', undefined, true);
  const edsMessage = buildReturnMessage(tenderedAmount, dueAmount, paymentTranId);
  return emeraldSendPaymentRequest('returns', edsMessage, span.spanContext())
    .catch(err => {
      logError(err, 'CCL Return Message Failed');
      err.code = EpsRefundFailure;
      throw err;
    })
    .finally(() => endSpan(span));
};

export const endPaymentSession = async (): Promise<void> => {
  const span = createSpan('[RPS] end-payment-session', undefined, true);
  if (store.getState().pinPad.sessionTransactionId) {
    try {
      const message = buildBasicPaymentMessage();
      store.dispatch(PinPadActions.removeSessionTranId());
      await emeraldSendPaymentRequest('end-payment-session', message, span.spanContext());
      isInPaymentSession = false;
      // need to reset the card data and swipe ahead tender
      resetCardData();
    } catch (err) {
      logError(err, 'CCL EndPaymentSession Message Failed');
    }
    resetCardData();
  }
  endSpan(span);
};

export const voidPayment = async (
  paymentTranId: string,
  sessionTranId: string,
): Promise<PaymentMessage> => {
  const span = createSpan('[RPS] void-payment', undefined, true);
  const message = buildBasicPaymentMessage();
  message.paymentDetails = { paymentTranID: paymentTranId, sessionTranID: sessionTranId };
  return emeraldSendPaymentRequest('void', message, span.spanContext())
    .catch(err => {
      logError(err, 'CCL Void Message Failed');
      if (err.code === EpsVoidFailure) {
        throw new PaymentException({
          code: EpsVoidFailure,
          message: 'EPS_VoidDeclined',
        });
      }

      throw err;
    })
    .finally(() => endSpan(span));
};

export const cancelPinpadOperation = async (): Promise<void> => {
  if (isPendingPaymentRequest()) {
    setCancelled(true);
    const span = createSpan('[RPS] cancel-pinpad-operation', undefined, true);
    try {
      const message = buildBasicPaymentMessage();
      message.paymentDetails = { paymentTranID: '', sessionTranID: '' };
      await emeraldSendPaymentRequest('cancel', message, span.spanContext());
    } catch (err) {
      logError(err, 'CCL Cancel Message Failed');
      throw err;
    } finally {
      endSpan(span);
    }
  }
};

export const enableCardEntry = async (): Promise<void> => {
  const span = createSpan('[RPS] enable-card', undefined, true);
  try {
    const message = buildBasicPaymentMessage();
    setEnableCardEntryPerformed(true);
    await emeraldSendPaymentRequest('enable-card-entry', message, span.spanContext());
  } catch (err) {
    setEnableCardEntryPerformed(false);
    logError(err, 'CCL EnableCardEntry Message Failed');
  } finally {
    endSpan(span);
  }
};

export const getSignatureFromPinPad = async (paymentTranId: string): Promise<SignatureDetails> => {
  const span = createSpan('[RPS] pinpad-signature', undefined, true);
  const message = buildBasicPaymentMessage();
  const paymentDetails: PaymentDetails = {
    paymentTranID: paymentTranId,
  };
  message.paymentDetails = paymentDetails;
  return emeraldSendPaymentRequest('get-signature', message, span.spanContext())
    .then(paymentMessage => paymentMessage.signatureDetails ?? {})
    .catch(err => {
      if (err.statusCode === PmiStatusCodes.Cancelled) {
        err.code = EpsSignatureCancel;
      } else {
        err.code = EpsSignatureFailure;
      }
      logError(err, 'CCL GetSignature Message Failed');
      throw err;
    })
    .finally(() => endSpan(span));
};

const logError = (err: any, message: string): void => {
  const loggingInput = {
    message,
    source: 'devices/payment/pinpad-operations.ts',
    data: err,
  };

  if (err?.message === 'Touchpoint missing CCL lane') {
    XLRLogger.debug(loggingInput);
  } else {
    XLRLogger.error(loggingInput);
  }
};

export const getPrescriptionFromPinPad = async (paymentTranId: string): Promise<PaymentMessage> => {
  const span = createSpan('[RPS] get-prescription-pinpad', undefined, true);
  const message = buildBasicPaymentMessage();
  message.paymentDetails = {
    paymentTranID: paymentTranId,
  };
  return emeraldSendPaymentRequest('get-prescription', message, span.spanContext())
    .catch(err => {
      logError(err, 'CCL GetPrescription Message Failed');
      throw err;
    })
    .finally(() => endSpan(span));
};

export const getPrescriptionInquiryFromPinPad = async (
  paymentTranId: string,
): Promise<PaymentMessage> => {
  const span = createSpan('[RPS] get-prescription-inquiry-pinpad', undefined, true);
  const message = {
    ...buildBasicPaymentMessage(),
  };
  if (paymentTranId) {
    message.paymentDetails = {
      paymentTranID: paymentTranId,
    };
  }
  return emeraldSendPaymentRequest('prescription-inquiry', message, span.spanContext())
    .catch(err => {
      logError(err, 'CCL GetPrescriptionInquiry Message Failed');
      throw err;
    })
    .finally(() => {
      endSpan(span);
    });
};

export const completePrescriptionFromPinPad = async (
  completeEwicRx: completeEwicRxDto,
): Promise<PaymentMessage> => {
  const span = createSpan('[RPS] complete-prescription-pinpad', undefined, true);
  try {
    const message = {
      ...buildBasicPaymentMessage(),
      amounts: {
        amountTendered: completeEwicRx.tenderedAmount.toString(),
        amountDue: completeEwicRx.dueAmount.toString(),
      },
      paymentDetails: {
        paymentTranID: completeEwicRx.paymentTranId,
      },
      ewicData: completeEwicRx.ewicData,
    };

    return emeraldSendPaymentRequest('complete-prescription', message, span.spanContext());
  } catch (err) {
    logError(err, 'CCL GetPrescription Message Failed');
    throw err;
  } finally {
    endSpan(span);
  }
};

export const balanceInquiryFromPinPad = async (
  externalId?: string,
  paymentTranId?: string,
  sessionTranId?: string,
): Promise<PaymentMessage> => {
  const span = createSpan('[RPS] balance-inquiry-pinpad', undefined, true);
  try {
    const message = {
      ...buildBasicPaymentMessage(),
    };

    if (paymentTranId) {
      message.paymentDetails = {
        paymentTranID: paymentTranId,
      };
    }
    if (sessionTranId) {
      message.paymentDetails = { ...message.paymentDetails, sessionTranID: sessionTranId };
    }

    if (externalId) {
      const cardDetails: CardDetails = {
        pan: externalId,
      };
      message.cardDetails = [cardDetails];
    }

    return await emeraldSendPaymentRequest('balance-inquiry', message, span.spanContext());
  } catch (err) {
    logError(err, 'CCL BalanceInquiry Message Failed');
    throw err;
  } finally {
    endSpan(span);
  }
};

export const promptPhoneNumber = async (): Promise<PaymentMessage> => {
  const span = createSpan('[RPS] promptPhoneNumber-pinpad', undefined, true);
  const message = {
    ...buildBasicPaymentMessage(),
  };
  return emeraldSendPaymentRequest('prompt-phone-number', message, span.spanContext())
    .catch(err => {
      logError(err, 'CCL promptPhoneNumber Message Failed');
      throw err;
    })
    .finally(() => endSpan(span));
};

export const setPinPadTrainingMode = async (trainingMode: boolean): Promise<PaymentMessage> => {
  const span = createSpan('[RPS] setTrainingMode-pinpad', undefined, true);
  const message = {
    ...buildBasicPaymentMessage(),
    trainingMode,
  };
  return emeraldSendPaymentRequest('set-training-mode', message, span.spanContext())
    .catch(err => {
      logError(err, 'CCL setTrainingMode Message Failed');
      throw err;
    })
    .finally(() => endSpan(span));
};

export const signatureNotVerified = async (): Promise<PaymentMessage> => {
  const span = createSpan('[RPS] signatureNotVerified-pinpad', undefined, true);
  const message = buildBasicPaymentMessage();

  return emeraldSendPaymentRequest('signature-non-verification', message, span.spanContext())
    .catch(err => {
      logError(err, 'CCL signatureNotVerified message failed');
      throw err;
    })
    .finally(() => endSpan(span));
};

export interface completeEwicRxDto {
  paymentTranId: string;
  tenderedAmount: number;
  dueAmount: number;
  ewicData: string;
}
export interface authorizeOnlineItemDto {
  externalId: string;
  price: string;
  entryMethod?: EntryMethod;
  upc?: string;
}
